import { Skeleton } from '@chakra-ui/react'
import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { EnumerationTypes, EnumerationValues, Ticket, Tickets } from 'stylewhere/api'
import { Button, Modal, Select, TextArea } from 'stylewhere/components'
import { __ } from 'stylewhere/i18n'
import { AppStore } from 'stylewhere/shared'
import { showToast, showToastError } from 'stylewhere/utils'

interface BugReportModalProps {
  visible: boolean
  onClose: () => void
}

export const BugReportModal: React.FC<BugReportModalProps> = ({ visible, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const name = AppStore.loggedUser ? `${AppStore.loggedUser.name} ${AppStore.loggedUser.surname || ''}`.trim() : ''
  const email = AppStore.loggedUser?.email || ''
  const [description, setDescription] = useState('')
  const [reasons, setReasons] = useState<{ code: string; description: string }[]>()
  const [selectedReason, setSelectedReason] = useState<{ code: string; description: string }>()

  const fetchReasons = async () => {
    try {
      let _reasons: { code: string; description: string }[] = []
      const response = await EnumerationTypes.getByCode('bug-report-reasons')
      if (response.content.length > 0) {
        const values = await EnumerationValues.getByEnumerationTypeId(response.content[0].id)
        if (values.content.length > 0) {
          _reasons = values.content.map((value) => ({ code: value.code, description: value.description }))
        }
      }
      setReasons(_reasons)
    } catch (error) {
      console.error('Errore nel fetch delle reasons del bug report:', error)
      setReasons([])
    }
  }

  useEffect(() => {
    fetchReasons()
  }, [visible])

  const sendToStylewhere = async () => {
    const payload: Ticket = {
      reason: selectedReason?.code ?? '',
      body: description,
      attributes: {
        userName: name,
        userEmail: email
      }
    }
    if (AppStore.defaultWorkstation) {
      payload.attributes.workstationCode = AppStore.defaultWorkstation.code
    }
    return Tickets.create(payload)
  }

  const handleSubmit = async () => {
    if (!AppStore.loggedUser?.id) {
      showToastError(__('È necessario essere autenticati per inviare feedback'))
      return
    }

    try {
      setIsSubmitting(true)
      const promises: Promise<any>[] = []

      promises.push(sendToStylewhere())

      await Promise.all(promises)

      onClose()
      showToast({
        title: __('titles.report_problem'),
        description: __('messages.sentry.success'),
        status: 'success',
        duration: 5000
      })

      setDescription('')
    } catch (error) {
      console.error("Errore nell'invio del feedback:", error)
      showToastError(__('error.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      closeOnOverlayClick={false}
      size="2xl"
      title={__('titles.report_problem')}
      visible={visible}
      onClose={onClose}
    >
      <ModalWrapper>
        <Container>
          <Description>{__('misc.sentry.report_problem')}</Description>
          {!reasons && <Skeleton height={16} width={555} borderRadius={10} />}
          {reasons && reasons.length > 0 && (
            <Select
              disabled={isSubmitting}
              label={__('misc.reason')}
              options={reasons}
              onSelect={(reason) => setSelectedReason(reason)}
              value={selectedReason}
              config={{ value: 'code', label: 'description', secondaryLabel: 'code' }}
              multiple={false}
            />
          )}
          <TextAreaWrapper>
            <TextArea
              disabled={isSubmitting}
              label={__('misc.description')}
              defaultValue={description}
              onChange={setDescription}
              required
            />
          </TextAreaWrapper>
          <ButtonContainer>
            <Button onClick={onClose} variant="secondary">
              {__('misc.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !description.trim()}>
              {isSubmitting ? __('button.submitting') : __('button.submit')}
            </Button>
          </ButtonContainer>
        </Container>
      </ModalWrapper>
    </Modal>
  )
}

const ModalWrapper = styled.div`
  max-width: 90vw;
`

const TextAreaWrapper = styled.div`
  textarea {
    min-height: 120px !important;
    resize: vertical;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
  margin-bottom: 8px;
`
